import {
  AudioTransformDevice,
  Device,
  VideoTransformDevice,
} from "amazon-chime-sdk-js";
import React, { useEffect, useState } from "react";
import { OptionType, Select } from "@themes/pollinator/ui/Select";
import { getDeviceId } from "amazon-chime-sdk-component-library-react";
import { Label } from "@themes/pollinator/ui/Label";
import { DeviceType } from "../../types";

interface Props {
  name: string;
  label: string;
  notFoundMsg: string;
  devices: DeviceType[];
  selectedDevice:
    | Device
    | AudioTransformDevice
    | VideoTransformDevice
    | string
    | null
    | undefined;
  onChange: (deviceId: string) => void;
  disabled?: boolean;
}

export const DeviceInput: React.FC<React.PropsWithChildren<Props>> = ({
  name,
  onChange,
  label,
  devices,
  selectedDevice,
  notFoundMsg,
  disabled,
  ...rest
}) => {
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");

  useEffect(() => {
    const getSelectedDeviceId = async (): Promise<void> => {
      // If selectedDevice is already a string (device ID), use it directly
      if (typeof selectedDevice === "string") {
        setSelectedDeviceId(selectedDevice);
      } else {
        // If it's a Chime SDK device object, extract the device ID
        const deviceId = await getDeviceId(selectedDevice);
        setSelectedDeviceId(deviceId);
      }
    };

    getSelectedDeviceId();
  }, [selectedDevice]);

  const deviceList = devices.map((device) => ({
    value: device.deviceId,
    label: device.label.trim() || label,
  }));

  const options = deviceList.length
    ? deviceList
    : [{ value: "not-available", label: notFoundMsg }];

  const selectDevice = (option: OptionType | OptionType[] | null): void => {
    const deviceId = (option as OptionType)?.value;

    if (deviceId === "not-available") {
      return;
    }
    onChange(deviceId);
  };

  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={name} id={`${name}-label`}>
        {label}
      </Label>
      <Select
        name={name}
        aria-labelledby={`${name}-label`}
        onChange={selectDevice}
        disabled={disabled}
        value={
          options.find((option) => option.value === selectedDeviceId) ||
          options.find((option) => option.value === "default")
        }
        options={options}
        {...rest}
      />
    </div>
  );
};

export default DeviceInput;
